import { z } from 'zod';

// Mirrors BE concernEnum (packages/db/src/schema/sessions.ts).
// bipolar_disorder is corporate-only; campus FE collapses it to "Others" in display.
export const concernEnum = z.enum([
  'anxiety_worry',
  'academic_stress',
  'relationship_issues',
  'depression',
  'family_conflict',
  'self_esteem',
  'sleep_disturbance',
  'substance_concerns',
  'grief_loss',
  'bipolar_disorder',
  'enquiry',
  'wellness_followup',
  'stress',
  'career',
  'clinical',
  'medical_health',
  'suicidal_self_harm',
  'others',
  // Ended before any concern surfaced; what the "Not assigned" backfill records
  // when there is nothing clinical to put down.
  'unknown',
  // Added 0070: expanded post-session picker.
  'emotional_distress',
  'anger',
  'emotional_regulation',
  'overthinking',
  'loneliness',
  'motivation',
  'exam_anxiety',
  'concentration',
  'workplace_stress',
  'job_dissatisfaction',
  'burnout',
  'breakup_separation',
  'friendship_interpersonal',
  'communication_difficulties',
  'socialization_difficulties',
  'boundary_issues',
  'adjustment_difficulties',
  'family_expectations_pressure',
  'financial_stress',
  'body_image',
  'eating_appetite',
  'self_harm',
  'suicidal_ideation',
  'personal_development',
  'general_emotional_support',
  // Added 0076: working-learner concerns, then postpartum depression and NSSI.
  'tuition_stress',
  'roi_anxiety',
  'job_security',
  'academic_reentry_anxiety',
  'comparative_performance_anxiety',
  'institutional_navigation_anxiety',
  'manager_support_gap',
  'peer_disconnect',
  'cognitive_overload',
  'role_identity_conflict',
  'health_strain',
  'stalled_progress',
  'existential_stress',
  'postpartum_depression',
  'non_suicidal_self_injury',
]);
export type ConcernKey = z.infer<typeof concernEnum>;

// Display labels for the concern picker. Order matches the enum so the dropdown
// reads in priority of clinical frequency.
export const CONCERN_LABELS: Record<ConcernKey, string> = {
  anxiety_worry: 'Anxiety / Worry',
  academic_stress: 'Academic Stress',
  relationship_issues: 'Relationship Issues',
  depression: 'Depression',
  family_conflict: 'Family Conflict',
  self_esteem: 'Self-Esteem',
  sleep_disturbance: 'Sleep Disturbance',
  substance_concerns: 'Substance Concerns',
  grief_loss: 'Grief / Loss',
  bipolar_disorder: 'Bipolar Disorder',
  enquiry: 'Enquiry',
  wellness_followup: 'Wellness Followup',
  stress: 'Stress',
  career: 'Career',
  clinical: 'Clinical',
  medical_health: 'Medical / Health',
  suicidal_self_harm: 'Suicidal Ideation / Self Harm',
  others: 'Others',
  unknown: 'Unknown',
  emotional_distress: 'Emotional Distress',
  anger: 'Anger',
  emotional_regulation: 'Emotional Regulation',
  overthinking: 'Overthinking',
  loneliness: 'Loneliness',
  motivation: 'Motivation',
  exam_anxiety: 'Exam Anxiety',
  concentration: 'Concentration',
  workplace_stress: 'Workplace Stress',
  job_dissatisfaction: 'Job Dissatisfaction',
  burnout: 'Burnout',
  breakup_separation: 'Breakup / Separation',
  friendship_interpersonal: 'Friendship / Interpersonal Issues',
  communication_difficulties: 'Communication Difficulties',
  socialization_difficulties: 'Socialization Difficulties',
  boundary_issues: 'Boundary Issues',
  adjustment_difficulties: 'Adjustment Difficulties',
  family_expectations_pressure: 'Family Expectations / Pressure',
  financial_stress: 'Financial Stress',
  body_image: 'Body Image',
  eating_appetite: 'Eating / Appetite Concerns',
  self_harm: 'Self-Harm',
  suicidal_ideation: 'Suicidal Ideation',
  personal_development: 'Personal Development',
  general_emotional_support: 'General Emotional Support',
  tuition_stress: 'Tuition Stress',
  roi_anxiety: 'ROI Anxiety',
  job_security: 'Job Security',
  academic_reentry_anxiety: 'Academic Re-entry Anxiety',
  comparative_performance_anxiety: 'Comparative Performance Anxiety',
  institutional_navigation_anxiety: 'Institutional Navigation Anxiety',
  manager_support_gap: 'Manager Support Gap',
  peer_disconnect: 'Peer Disconnect',
  cognitive_overload: 'Cognitive Overload',
  role_identity_conflict: 'Role Identity Conflict',
  health_strain: 'Health Strain',
  stalled_progress: 'Stalled Progress',
  existential_stress: 'Existential Stress',
  postpartum_depression: 'Postpartum Depression',
  non_suicidal_self_injury: 'NSSI (Non-Suicidal Self-Injury)',
};

export const CONCERN_OPTIONS: { value: ConcernKey; label: string }[] = (
  Object.keys(CONCERN_LABELS) as ConcernKey[]
).map((value) => ({ value, label: CONCERN_LABELS[value] }));
